import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Location } from '@angular/common';
import { of, take } from 'rxjs';
import { ColumnSelection, PrintColumnSelectionMode } from '../../models/dialog.model';
import { getPrintColumnKey, getPrintColumnLabel } from '../../utils/print-column.util';
import { PrintAuditService } from '../../services/print-audit.service';

/**
 * PrintPreviewBarComponent
 *
 * The screen-only chrome above a print preview: what you are looking at, what will be printed,
 * and the two things you can do about it. Replaces the menu that used to open over the preview
 * on arrival — the column list expands inline here instead, so the page it configures stays
 * visible while you configure it.
 *
 * Nothing in this component prints; the printed document is whatever the host screen renders.
 */
@Component({
  selector: 'app-print-preview-bar',
  templateUrl: './print-preview-bar.component.html',
  styleUrl: './print-preview-bar.component.scss'
})
export class PrintPreviewBarComponent implements OnChanges {
  /** Screen being previewed. Titles the bar and names the audit entry. */
  @Input() viewName = '';
  /** Where Back returns to, when that is not the previewed screen itself. */
  @Input() backTo = '';
  /** Study the preview belongs to, shown beside the title. */
  @Input() studyName = '';
  /** Anything further that identifies the document — panel, spec revision, operator. */
  @Input() contextLine = '';
  @Input() tableColumns: ColumnSelection[] = [];
  @Input() enableColumnSelection = true;
  /** Off where the screen already audits its own action, e.g. Export CofA. */
  @Input() auditPrint = true;
  @Input() columnSelectionMode: PrintColumnSelectionMode = 'kitLotInstrumentBead';

  @Output() selectColumns = new EventEmitter<ColumnSelection[]>();

  protected panelOpen = false;
  protected selectedKeys = new Set<string>();

  /** Past this many options the panel would push the preview off screen, so it scrolls instead. */
  private readonly scrollAfterColumns = 60;

  constructor(
    private location: Location,
    private printAuditService: PrintAuditService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tableColumns']) {
      this.selectedKeys = new Set(
        (this.tableColumns ?? []).filter(column => column.isSelected).map(column => getPrintColumnKey(column))
      );
    }
  }

  protected get hasColumns(): boolean {
    return this.enableColumnSelection && !!this.tableColumns?.length;
  }

  protected get selectedCount(): number {
    return this.selectedKeys.size;
  }

  protected get totalCount(): number {
    return this.tableColumns?.length ?? 0;
  }

  protected get columnsScroll(): boolean {
    return this.totalCount > this.scrollAfterColumns;
  }

  /** With no column selected there is nothing on the page to print. */
  protected get canPrint(): boolean {
    return !this.hasColumns || this.selectedCount > 0;
  }

  protected label(column: ColumnSelection): string {
    return getPrintColumnLabel(column, this.columnSelectionMode);
  }

  protected isChecked(column: ColumnSelection): boolean {
    return this.selectedKeys.has(getPrintColumnKey(column));
  }

  protected trackByKey(index: number, column: ColumnSelection): string {
    return getPrintColumnKey(column);
  }

  protected togglePanel(): void {
    this.panelOpen = !this.panelOpen;
  }

  protected toggleColumn(column: ColumnSelection, checked: boolean): void {
    const key = getPrintColumnKey(column);
    if (checked) {
      this.selectedKeys.add(key);
    } else {
      this.selectedKeys.delete(key);
    }
    this.emitSelection();
  }

  protected selectAll(): void {
    this.selectedKeys = new Set((this.tableColumns ?? []).map(column => getPrintColumnKey(column)));
    this.emitSelection();
  }

  protected clearAll(): void {
    this.selectedKeys = new Set();
    this.emitSelection();
  }

  protected print(): void {
    if (!this.canPrint) {
      return;
    }
    const audited$ = this.auditPrint ? this.printAuditService.logPrint(this.viewName) : of(undefined);
    audited$.pipe(take(1)).subscribe(() => {
      // Let the host screen finish laying out any selection made a moment ago before the
      // browser snapshots the page.
      setTimeout(() => window.print(), 500);
    });
  }

  protected goBack(): void {
    this.location.back();
  }

  /** The panel edits selection live, so every change republishes the whole set. */
  private emitSelection(): void {
    this.selectColumns.emit(
      (this.tableColumns ?? []).map(column => ({
        ...column,
        isSelected: this.selectedKeys.has(getPrintColumnKey(column))
      }))
    );
  }
}
